<template lang="pug">
.flex.gap-2.items-center(v-if="user" v-tooltip.top="showTooltip ? tooltipText(user) : ''" v-bind="attrs")
  Avatar(
    v-if="showAvatar"
    shape="circle"
    :class="cssClass"
    :style="initialsStyle"
    :label="showInitials ? user?.initials : null"
    :image="showInitials ? null : avatarImage"
  )

  div(v-if="showFullName || showShortName" :class="props.textClass")
    div(v-if="showFullName") {{ user?.fullName }}
    div(v-if="showShortName") {{ user?.shortName }}
    .text-sm.text-canvas-400.mt-0(v-if="showRoles") {{ roleFullNames }}
.flex.gap-2.items-center(v-else v-bind="attrs")
  .flex.gap-2.items-center
    Avatar(
      v-if="showAvatar"
      shape="circle"
      icon="ph ph-user" 
      :class="cssClass"
      style="background-color: #9ca3af; color: #ffffff;"
    )
    .text-sm.text-canvas-400.italic(v-if="showFullName || showShortName") --No User-- 

//- pre showInitials: {{ showInitials }}
//- pre user: {{ user }}
//- pre userRoles: {{ userRoles }}
</template>

<script setup>
import { computed, useAttrs } from 'vue'
import { roleNamesMap } from '@/lib/constants'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const props = defineProps({
  user:          { type: [Object, null],  required: true, default: {} },
  size:          { type: String,  default: 'normal' },
  avatarType:    { type: String,  default: 'image' },
  textClass:     { type: String,  default: '' },
  showTooltip:   { type: [Boolean, String], default: false },
  showAvatar:    { type: [Boolean, String], default: true },
  showFullName:  { type: [Boolean, String], default: false },
  showShortName: { type: [Boolean, String], default: false },
  showRoles:     { type: [Boolean, String], default: false },
})

const userRoles     = computed(() => props.user.roles)
const roleFullNames = computed(() => userRoles.value?.map(r => r.fullName).sort().join(', '))

//-------------------------
// Avatar Initials
//-------------------------
// Set of color schemes to use if we show initials as the avatar
// v2 — slightly darker (Tailwind 600-level)
const INITIALS_COLORS = [
  { bg: '#4f46e5', text: '#ffffff' }, // indigo-600
  { bg: '#059669', text: '#ffffff' }, // emerald-600
  { bg: '#0284c7', text: '#ffffff' }, // sky-600
  { bg: '#7c3aed', text: '#ffffff' }, // violet-600
  { bg: '#ea580c', text: '#ffffff' }, // orange-600
  { bg: '#0d9488', text: '#ffffff' }, // teal-600
  { bg: '#db2777', text: '#ffffff' }, // pink-600
  { bg: '#65a30d', text: '#ffffff' }, // lime-600
  { bg: '#0891b2', text: '#ffffff' }, // cyan-600
  { bg: '#9333ea', text: '#ffffff' }, // purple-600
]
// v1 — original (Tailwind 500-level)
// const INITIALS_COLORS = [
//   { bg: '#6366f1', text: '#ffffff' }, // indigo-500
//   { bg: '#10b981', text: '#ffffff' }, // emerald-500
//   { bg: '#0ea5e9', text: '#ffffff' }, // sky-500
//   { bg: '#8b5cf6', text: '#ffffff' }, // violet-500
//   { bg: '#f97316', text: '#ffffff' }, // orange-500
//   { bg: '#14b8a6', text: '#ffffff' }, // teal-500
//   { bg: '#ec4899', text: '#ffffff' }, // pink-500
//   { bg: '#84cc16', text: '#ffffff' }, // lime-500
//   { bg: '#06b6d4', text: '#ffffff' }, // cyan-500
//   { bg: '#a855f7', text: '#ffffff' }, // purple-500
// ]

// Sums the Unicode code point of each character to produce a stable integer —
// the same initials always yield the same number, so color assignment never changes.
function hashInitials(initials) {
  return (initials || '').split('').reduce((sum, c) => sum + c.charCodeAt(0), 0)
}

const showInitials = computed(() => props.avatarType === 'initials' || !avatarImage.value)

const initialsStyle = computed(() => {
  if (!showInitials.value) return {}
  // Modulo maps the hash integer to a valid palette index regardless of palette size.
  const { bg, text } = INITIALS_COLORS[hashInitials(props.user?.initials) % INITIALS_COLORS.length]
  return { backgroundColor: bg, color: text }
})

//-------------------------
// Avatar Sizing
//-------------------------
const SIZE_TO_VARIANT = {
  mini:   'small',
  small:  'small',
  normal: 'medium',
  large:  'medium',
  '2xl':  'large',
  '3xl':  'large',
  expand: 'large',
}

const avatarImage = computed(() => {
  const variantKey = SIZE_TO_VARIANT[props.size] || 'medium'
  return props.user?.avatarUrls?.[variantKey] ?? props.user?.avatarUrl ?? null
})

const cssClass = computed(() => {
  const strArr = []
  switch (props.size) {
    case 'mini':
      strArr.push('p-avatar-mini')
      break
    case 'small':
      strArr.push('p-avatar-small')
      break
    case 'large':
      strArr.push('p-avatar-large')
      break
    case '2xl':
      strArr.push('p-avatar-2xl')
      break
    case '3xl':
      strArr.push('p-avatar-3xl')
      break
    case 'expand':
      strArr.push('p-avatar-expand')
      break
  }
  return strArr.join(' ')
})

function tooltipText(user) {
  return `${user.fullName} (${user.initials})`
}
</script>
